/**
 * Montagem do documento.
 *
 * Função pura: documento validado entra, HTML autocontido sai. Sem DOM, sem rede, sem sistema de
 * arquivos — é o que permite rodar no navegador (preview) e na Lambda (artefato publicado) com o
 * mesmo código, eliminando a divergência entre o que o vendedor aprova e o que o cliente recebe
 * (decisão D3).
 */

import { condicoesComerciaisTexto, type DocumentoProposta } from '@emaster/dominio';

import {
  navegacao,
  heroi,
  rodape,
  secaoAvaliacao,
  secaoContato,
  secaoEscopo,
  secaoProximosPassos,
  secaoTexto,
  scriptNavegacaoInterna,
  type ItemNavegacao,
} from './componentes.ts';
import { escaparHtml, sanitizarHtmlRico } from './escapar.ts';
import { corpoFormal, scriptAceite } from './formal.ts';
import { secaoAssessment } from './assessment.ts';
import { blocoInstitucional } from './institucional.ts';
import { secaoInvestimento } from './investimento.ts';
import { secaoSafeStorage } from './safestorage.ts';
import { blocoPlanoSuporte } from './suporte.ts';
import { secaoTreinamento } from './treinamento.ts';
import { cabecalhoDocumento, CONTAINER, rodapeDocumento } from './tokens.ts';
import { faixaVencimento, scriptVencimento } from './vencimento.ts';

/** Versão do template gravada no documento, para saber com qual layout a proposta foi emitida. */
export const VERSAO_TEMPLATE = '1.1.0';

export interface OpcoesRenderizacao {
  /**
   * `web` é a peça de navegação, com nav fixa e botão de impressão.
   * `formal` é o documento contratual que o cliente assina, conforme o modelo do Dynamics.
   */
  readonly variante?: 'web' | 'formal';
  /**
   * Data de referência em `AAAA-MM-DD`, para a sinalização de vencimento.
   * Injetável para teste determinístico. Sem ela, usa a data corrente em UTC.
   */
  readonly hoje?: string;
  /**
   * Data URL da imagem do desenho de arquitetura. Quando presente, renderiza o bloco antes do
   * investimento. A imagem é embutida no HTML como base64, ficando autocontida.
   */
  readonly arquiteturaDataUrl?: string;
  /**
   * Endpoint do aceite eletrônico, usado apenas na variante formal. Sem ele, o botão de aceite
   * explica que o recurso não está habilitado em vez de falhar.
   */
  readonly urlAceite?: string;
}

/** Observações do editor rich-text, sanitizadas. */
function secaoObservacoes(html: string | null): string {
  if (html === null) return '';
  const limpo = sanitizarHtmlRico(html);
  if (limpo === '') return '';

  return `
    <section id="observacoes" class="scroll-mt-24 py-20 bg-white border-t border-gray-200">
        <div class="${CONTAINER}">
            <div class="mb-8 max-w-3xl">
                <span class="text-sm font-semibold text-brand-accent uppercase tracking-wider">Observações</span>
                <h2 class="text-3xl font-bold text-brand-blue mt-2 mb-4">Considerações e condições</h2>
                <div class="w-20 h-1.5 bg-brand-accent rounded-full"></div>
            </div>
            <div class="max-w-3xl text-gray-600 leading-relaxed space-y-4">
                ${limpo}
            </div>
        </div>
    </section>`;
}

/** Monta os itens da nav a partir das seções que realmente existem no documento. */
function itensNavegacao(doc: DocumentoProposta): ItemNavegacao[] {
  const itens: ItemNavegacao[] = doc.conteudo.secoes.map((s) => ({
    ancora: s.ancora,
    rotulo: s.eyebrow,
  }));

  // SafeStorage aparece só quando o produto está na proposta, e antes do suporte: é a ordem em que as
  // seções são renderizadas, e a nav precisa refletir a ordem do documento para as âncoras fazerem
  // sentido ao rolar.
  if (doc.premissas.safestorage !== null) {
    itens.push({ ancora: 'safestorage', rotulo: 'SafeStorage' });
  }

  // Suporte aparece só quando há plano contratado (não é 'nenhum').
  if (doc.premissas.planoSuporte !== 'nenhum') {
    itens.push({ ancora: 'suporte', rotulo: 'Suporte' });
  }

  /*
   * O assessment é cortesia e entra depois do suporte, antes do investimento: é o último argumento de
   * valor que o cliente lê antes de chegar ao preço.
   *
   * A condição repete a de `secaoAssessment`, incluindo a lista vazia. O schema recusa lista vazia,
   * mas a nav não pode depender disso: divergir da seção produziria item apontando para âncora que
   * não foi emitida, e o CTA do herói herdaria o link morto.
   */
  const focosAssessment = doc.premissas.assessment;
  if (focosAssessment !== null && focosAssessment.length > 0) {
    itens.push({ ancora: 'assessment', rotulo: 'Assessment' });
  }

  // Treinamento, também cortesia, vem depois do assessment. Zero significa não incluído.
  if (doc.premissas.sessoesTreinamento > 0) {
    itens.push({ ancora: 'treinamento', rotulo: 'Treinamento' });
  }

  itens.push({ ancora: 'investimento', rotulo: 'Investimento' });

  if (doc.conteudo.escopoIncluido.length > 0 || doc.conteudo.escopoExcluido.length > 0) {
    itens.push({ ancora: 'escopo', rotulo: 'Escopo' });
  }
  if (doc.conteudo.proximosPassos.length > 0) {
    itens.push({ ancora: 'proximos-passos', rotulo: 'Próximos Passos' });
  }
  itens.push({ ancora: 'contato', rotulo: 'Contato' });

  return itens;
}

/**
 * Renderiza a proposta.
 *
 * @param doc Documento já validado por `validarDocumento`. Renderizar documento não validado é erro
 * de programação: o schema é a fronteira.
 */
/** Seção com o desenho de arquitetura, quando fornecido. */
function secaoArquitetura(dataUrl: string | undefined): string {
  if (dataUrl === undefined || dataUrl === '') return '';

  return `
    <section class="py-20 bg-white border-t border-gray-200">
        <div class="${CONTAINER}">
            <div class="mb-10 max-w-3xl">
                <span class="text-sm font-semibold text-brand-accent uppercase tracking-wider">Arquitetura</span>
                <h2 class="text-3xl font-bold text-brand-blue mt-2 mb-4">Desenho da solução proposta</h2>
                <div class="w-20 h-1.5 bg-brand-accent rounded-full"></div>
                <p class="text-gray-600 text-lg leading-relaxed mt-6">
                    Diagrama técnico que ilustra os serviços, as conexões e o fluxo de dados do ambiente proposto.
                </p>
            </div>
            <div class="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 overflow-hidden">
                <img src="${dataUrl}" alt="Diagrama de arquitetura da solução" class="w-full rounded-xl" />
            </div>
        </div>
    </section>`;
}

export function renderizarProposta(
  doc: DocumentoProposta,
  opcoes: OpcoesRenderizacao = {},
): string {
  const variante = opcoes.variante ?? 'web';

  const titulo = escaparHtml(`Proposta Comercial | ${doc.projeto} para ${doc.cliente}`);
  const descricao = escaparHtml(
    `Proposta ${doc.numero} da eMaster Cloud & Security para ${doc.cliente}. Válida até ${doc.validaAte}.`,
  );

  // A variante formal é documento contratual, não peça de convencimento: não tem herói, bloco
  // institucional, gráfico nem navegação. Ver o cabeçalho de `formal.ts`.
  if (variante === 'formal') {
    return [
      cabecalhoDocumento(titulo, descricao),
      corpoFormal(doc, condicoesComerciaisTexto(doc).split('\n')),
      rodapeDocumento([scriptAceite(opcoes.urlAceite ?? null)]),
    ].join('\n');
  }

  // Alternância de fundo entre seções, para criar ritmo de leitura.
  const secoes = doc.conteudo.secoes
    .map((s, indice) =>
      secaoTexto(
        s.ancora,
        s.eyebrow,
        s.titulo,
        s.paragrafos,
        indice % 2 === 0 ? 'branco' : 'cinza',
      ),
    )
    .join('');

  /*
   * A nav é a lista ordenada das seções que este documento realmente tem, então o primeiro item é
   * exatamente onde começa a narrativa — o destino do botão "Conhecer a proposta".
   *
   * A ordem em `itensNavegacao` faz o fallback acontecer sozinho: sem seções editoriais, o primeiro
   * item é o plano de suporte; sem plano contratado, é o investimento. Nenhum dos três pode virar
   * link morto, porque a lista só inclui seção que é renderizada.
   */
  const itensNav = itensNavegacao(doc);
  const ancoraInicio = itensNav[0]?.ancora ?? 'investimento';

  const partes = [
    cabecalhoDocumento(titulo, descricao),
    faixaVencimento(doc, opcoes.hoje),
    navegacao(itensNav),
    heroi(doc, ancoraInicio),
    blocoInstitucional(doc.premissas.trilha),
    secoes,
    secaoSafeStorage(doc),
    blocoPlanoSuporte(doc.premissas.planoSuporte, doc.premissas.trilha),
    secaoAssessment(doc),
    secaoTreinamento(doc),
    secaoArquitetura(opcoes.arquiteturaDataUrl),
    secaoInvestimento(doc, opcoes.hoje),
    secaoEscopo(doc.conteudo.escopoIncluido, doc.conteudo.escopoExcluido),
    secaoObservacoes(doc.conteudo.observacoesHtml),
    secaoProximosPassos(doc.conteudo.proximosPassos, doc.conteudo.condicoesComerciais),
    secaoContato(doc.responsavel, doc.cliente),
    // Bloco de avaliação: aparece só na variante web. Na formal, pesquisa de satisfação não cabe
    // em documento de licitação/contratação — é peça jurídica, não relacional.
    secaoAvaliacao(doc.responsavel, doc.cliente),
    rodape(doc),
    // `botaoImprimir()` suspenso por decisão do usuário até a proposta formal com PDF entrar.
    rodapeDocumento([scriptVencimento(), scriptNavegacaoInterna()]),
  ];

  return partes.filter((p) => p !== '').join('\n');
}
